import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Assignment,
  Search,
  Settings,
  ExitToApp,
  Person,
  AdminPanelSettings,
  Message,
  Description,
  ExpandMore,
  Build,
  AcUnit,
  Thermostat,
  PlayCircleOutline,
  NoteAdd,
  Folder,
  People,
  ListAlt
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PWAInstallPrompt from '../components/PWAInstallPrompt';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [unreadCount, setUnreadCount] = useState(0);
  const [expandedCategories, setExpandedCategories] = useState(new Set()); // Alle zugeklappt beim Start

  // Accordion Handler
  const handleCategoryToggle = (categoryName) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryName)) {
      newExpanded.delete(categoryName);
    } else {
      newExpanded.add(categoryName);
    }
    setExpandedCategories(newExpanded);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadUnreadCount();
    // Refresh unread count every 10 seconds
    const interval = setInterval(loadUnreadCount, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const formatDateTime = (date) => {
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    const dateStr = date.toLocaleDateString('de-DE', options);
    const timeStr = date.toLocaleTimeString('de-DE');
    return `${dateStr}, ${timeStr}`;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Menu-Struktur mit Überkarten
  const menuCategories = [
    {
      category: 'Wartungen',
      color: '#4caf50',
      items: [
        {
          title: 'Wartung Klima',
          description: 'Neues Wartungsprotokoll Klimaanlage erstellen',
          icon: <AcUnit sx={{ fontSize: 48 }} />,
          color: '#4caf50',
          action: () => navigate('/protocol/customer-search')
        },
        {
          title: 'Protokolle Klima',
          description: 'Klimaanlagen-Protokolle ansehen und bearbeiten',
          icon: <ListAlt sx={{ fontSize: 48 }} />,
          color: '#42a5f5',  // Hellblau
          action: () => navigate('/protocol/klima-list')
        },
        {
          title: 'Wartung WP',
          description: 'Neues Wartungsprotokoll Wärmepumpe erstellen',
          icon: <Thermostat sx={{ fontSize: 48 }} />,
          color: '#81c784',
          action: () => navigate('/protocol/wp-customer-search')
        },
        {
          title: 'Protokolle WP',
          description: 'Wärmepumpen-Protokolle ansehen und bearbeiten',
          icon: <ListAlt sx={{ fontSize: 48 }} />,
          color: '#FFB74D',  // Blasses Orange
          action: () => navigate('/protocol/wp-list')
        }
      ]
    },
    {
      category: 'Inbetriebnahmen',
      color: '#00bcd4',
      items: [
        {
          title: 'Inbetriebnahme Klima',
          description: 'Inbetriebnahmeprotokoll Klimaanlage',
          icon: <PlayCircleOutline sx={{ fontSize: 48 }} />,
          color: '#00bcd4',
          action: () => navigate('/protocol/ibn-klima-customer-search')
        },
        {
          title: 'Inbetriebnahme WP',
          description: 'Inbetriebnahmeprotokoll Wärmepumpe',
          icon: <PlayCircleOutline sx={{ fontSize: 48 }} />,
          color: '#26c6da',
          action: () => navigate('/protocol/ibn-wp-customer-search')
        }
      ]
    },
    {
      category: 'Datenaufnahmen',
      color: '#8bc34a',
      items: [
        {
          title: 'Datenaufnahme Angebot',
          description: 'Klimaanlagen-Angebotserstellung',
          icon: <NoteAdd sx={{ fontSize: 48 }} />,
          color: '#8bc34a',
          action: () => navigate('/angebot/customer-search')
        },
        {
          title: 'AN Aufnahmen',
          description: 'Gespeicherte Angebote ansehen',
          icon: <Folder sx={{ fontSize: 48 }} />,
          color: '#9ccc65',
          action: () => navigate('/angebot-list')
        }
      ]
    },
    {
      category: 'Allgemein',
      color: '#2196f3',
      items: [
        {
          title: 'Kundensuche',
          description: 'Kunde suchen und verwalten',
          icon: <People sx={{ fontSize: 48 }} />,
          color: '#2196f3',
          action: () => navigate('/customers')
        },
        {
          title: 'Protokolle',
          description: 'Gespeicherte Protokolle ansehen',
          icon: <ListAlt sx={{ fontSize: 48 }} />,
          color: '#42a5f5',
          action: () => navigate('/protocols')
        },
        {
          title: 'Nachrichten',
          description: 'Interne Nachrichten senden und empfangen',
          icon: <Message sx={{ fontSize: 48 }} />,
          color: '#64b5f6',
          action: () => navigate('/messages')
        }
      ]
    }
  ];

  // Admin-Kategorie nur für Admins
  if (user?.role === 'admin') {
    menuCategories.push({
      category: 'Admin',
      color: '#d32f2f',
      items: [
        {
          title: 'Benutzerverwaltung',
          description: 'Benutzer anlegen und verwalten',
          icon: <AdminPanelSettings sx={{ fontSize: 48 }} />,
          color: '#d32f2f',
          action: () => navigate('/admin')
        },
        {
          title: 'Einstellungen',
          description: 'App-Einstellungen verwalten',
          icon: <Settings sx={{ fontSize: 48 }} />,
          color: '#e53935',
          action: () => navigate('/settings')
        }
      ]
    });
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
      
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div">
              Engelhardt Service-App
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {formatDateTime(currentDateTime)}
            </Typography>
          </Box>

          {/* Messages Badge */}
          <IconButton color="inherit" onClick={() => navigate('/messages')}>
            <Badge badgeContent={unreadCount} color="error">
              <Message />
            </Badge>
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person />
              <Typography variant="body1">
                {user?.full_name || user?.username}
              </Typography>
            </Box>
            <IconButton color="inherit" onClick={handleLogout} title="Abmelden">
              <ExitToApp />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h5" gutterBottom>
            Willkommen zurück, {user?.full_name || user?.username}!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Was möchten Sie heute tun?
          </Typography>
        </Paper>

        {/* Dashboard mit Kategorien als Accordions */}
        {menuCategories.map((category, catIndex) => (
          <Accordion 
            key={catIndex}
            expanded={expandedCategories.has(category.category)}
            onChange={() => handleCategoryToggle(category.category)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMore />}
              sx={{ 
                bgcolor: category.color, 
                color: 'white',
                '&:hover': {
                  bgcolor: category.color,
                  filter: 'brightness(0.95)'
                }
              }}
            >
              <Typography variant="h5" fontWeight="bold">
                {category.category} ({category.items.length})
              </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 3 }}>
              {/* Menu Items in dieser Kategorie */}
              <Grid container spacing={2}>
              {category.items.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <Card 
                    sx={{ 
                      height: 200,
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Box
                        sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: item.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      mb: 1.5,
                      color: 'white'
                    }}
                  >
                    {React.cloneElement(item.icon, { sx: { fontSize: 36 } })}
                  </Box>
                  <Typography variant="h6" component="div" gutterBottom sx={{ fontSize: '1.05rem' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
                    {item.description}
                  </Typography>
                  <Button 
                    variant="contained" 
                    onClick={item.action}
                    sx={{ 
                      bgcolor: item.color,
                      '&:hover': {
                        bgcolor: item.color,
                        filter: 'brightness(0.9)'
                      },
                      fontSize: '0.8rem',
                      py: 0.75
                    }}
                  >
                    Öffnen
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Info Box */}
        {user?.role === 'admin' && (
          <Paper sx={{ p: 2, mt: 3, bgcolor: '#fff3cd' }}>
            <Typography variant="body2">
              <strong>Admin-Modus:</strong> Sie haben Administratorrechte und können Benutzer verwalten.
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
}
